import { Component, Inject, OnInit, Type } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import { SidebarStateService } from '@core/services/sidebar-state.service';
import { ViewportService } from '@core/services/viewport.service';
import { MenuDrawerService } from '@core/services/menu-drawer.service';
import { MenuItem } from '@core/models/menu.model';
import { LocalizedModuleDto, LocalizedSubmoduleDto } from '@core/models/user.model';
import {
  backendIconToLucide,
  backendRouteToAngular,
  getDefaultRouteForModuleName,
  PRICE_TABLES_PERMISSION,
  SUBMODULE_KEY_CADASTRO_TABELA_PRECO,
} from '@core/utilities/backend-menu.util';
import { UserAccessService } from '@core/services/user-access.service';
import { PriceTablesModalService } from '@app/features/price-tables/services/price-tables-modal.service';
import { HealthFacilitiesModalService } from '@app/features/health-facilities/services/health-facilities-modal.service';
import { CollectionQueuesModalService } from '@app/features/collection-queues/services/collection-queues-modal.service';
import { VaccinesConfigModalService } from '@app/features/vaccines-config/services/vaccines-config-modal.service';
import { LaboratorySectorsModalService } from '@app/features/laboratory-sectors/services/laboratory-sectors-modal.service';
import { CashClosingModalService } from '@app/features/cash-control/services/cash-closing-modal.service';
import { CashMovementReportModalService } from '@app/features/cash-control/services/cash-movement-report-modal.service';
import { CreditDebitReportModalService } from '@app/features/cash-control/services/credit-debit-report-modal.service';
import { PatientRegistryModalService } from '@app/features/patient/services/patient-registry-modal.service';
import { SolicitantesModalService } from '@app/features/solicitantes/services/solicitantes-modal.service';
import { LAYOUT_OVERLAY_LOADERS, LayoutOverlayLoaders, componentWhileOpen } from './layout-overlays';
import { ClinicsModalService } from '@app/features/clinics/services/clinics-modal.service';


@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss',
    standalone: false
})
export class LayoutComponent implements OnInit {
  /**
   * Reads the persisted state from `SidebarStateService`. The LayoutComponent
   * is recreated on every navigation (it lives inside each feature page's
   * template), so initialising with a literal `true` would "reopen" the
   * sidebar after every redirect — a behaviour reported by the user.
   */
  get sidebarOpen(): boolean {
    return this.state.isOpen;
  }
  set sidebarOpen(value: boolean) {
    this.state.isOpen = value;
  }

  /**
   * Same backend menu the sidebar consumes, mapped to the `MenuItem` shape
   * used by the mobile drawer. Built lazily on first read so desktop pages
   * don't pay the request twice — `SidebarStateService.getModules()` is
   * already cached at the service level.
   */
  menuItems$!: Observable<MenuItem[]>;

  /**
   * Overlay page to mount while its modal service reports it open, `null`
   * otherwise. The page chunk is fetched on first open (see layout-overlays.ts).
   */
  healthFacilitiesPage$!: Observable<Type<unknown> | null>;
  collectionQueuesPage$!: Observable<Type<unknown> | null>;
  laboratorySectorsPage$!: Observable<Type<unknown> | null>;
  clinicsPage$!: Observable<Type<unknown> | null>;
  cashClosingModal$!: Observable<Type<unknown> | null>;
  cashMovementReportModal$!: Observable<Type<unknown> | null>;
  creditDebitReportModal$!: Observable<Type<unknown> | null>;
  vaccinesConfigPage$!: Observable<Type<unknown> | null>;
  patientRegistryWindow$!: Observable<Type<unknown> | null>;
  solicitantesPage$!: Observable<Type<unknown> | null>;
  priceTablesPage$!: Observable<Type<unknown> | null>;

  constructor(
    private state: SidebarStateService,
    private router: Router,
    public viewport: ViewportService,
    public menuDrawer: MenuDrawerService,
    // Hosts the Health Facilities listing overlay (TASK-60341). The template
    // mounts <app-health-facilities-page> only while `open$` is true, so the
    // modal renders on top of the current page without any navigation.
    public healthFacilitiesModal: HealthFacilitiesModalService,
    // Hosts the Collection Queues (Filas de Coleta, cadastro) listing overlay
    // (US #60630) — same pattern as healthFacilitiesModal above.
    public collectionQueuesModal: CollectionQueuesModalService,
    // Hosts the Vacinas configuration overlay (US #61577) — same pattern as the two
    // above: mounted only while open$ is true, and never navigated to.
    public vaccinesConfigModal: VaccinesConfigModalService,
    // Hosts the Setores do Laboratório overlay (US #61003) — same pattern as the three above.
    public laboratorySectorsModal: LaboratorySectorsModalService,
    // Overlay singleton of the Cadastro de Clinicas (TASK-60770) — same pattern as the ones above.
    public clinicsModal: ClinicsModalService,
    // Hosts the Fechamento de Caixa overlay (US #61506) — same pattern as the four above.
    public cashClosingModal: CashClosingModalService,
    // Hosts the Movimentação do Caixa overlay (US #61510, D-106) — opened by the Controle de Caixa header button.
    public cashMovementReportModal: CashMovementReportModalService,
    // Hosts the Créditos/Débitos overlay (US #61511, P-1) — opened by the Controle de Caixa header button.
    public creditDebitReportModal: CreditDebitReportModalService,
    // Hosts the Cadastro de Paciente window (US 62835), opened by the Recepção menu item.
    public patientRegistryModal: PatientRegistryModalService,
    // Hosts the Solicitantes overlay (Feature #59770) — same pattern as the ones above.
    public solicitantesModal: SolicitantesModalService,
    // Hosts the Tabela de Preços listing overlay (US #62189) — same pattern as the ones above.
    public priceTablesModal: PriceTablesModalService,
    // Gates the Tabela de Preços leaf of the mobile drawer (US #62189, decision 1).
    private userAccess: UserAccessService,
    @Inject(LAYOUT_OVERLAY_LOADERS) private overlayLoaders: LayoutOverlayLoaders,
  ) {}

  ngOnInit(): void {
    this.healthFacilitiesPage$ = componentWhileOpen(this.healthFacilitiesModal.open$, this.overlayLoaders.healthFacilities);
    this.collectionQueuesPage$ = componentWhileOpen(this.collectionQueuesModal.open$, this.overlayLoaders.collectionQueues);
    this.laboratorySectorsPage$ = componentWhileOpen(this.laboratorySectorsModal.open$, this.overlayLoaders.laboratorySectors);
    this.clinicsPage$ = componentWhileOpen(this.clinicsModal.open$, this.overlayLoaders.clinics);
    this.cashClosingModal$ = componentWhileOpen(this.cashClosingModal.open$, this.overlayLoaders.cashClosing);
    this.cashMovementReportModal$ = componentWhileOpen(
      this.cashMovementReportModal.open$,
      this.overlayLoaders.cashMovementReport
    );
    this.creditDebitReportModal$ = componentWhileOpen(
      this.creditDebitReportModal.open$,
      this.overlayLoaders.creditDebitReport
    );
    this.vaccinesConfigPage$ = componentWhileOpen(this.vaccinesConfigModal.open$, this.overlayLoaders.vaccinesConfig);
    this.patientRegistryWindow$ = componentWhileOpen(this.patientRegistryModal.open$, this.overlayLoaders.patientRegistry);
    this.solicitantesPage$ = componentWhileOpen(this.solicitantesModal.open$, this.overlayLoaders.solicitantes);
    this.priceTablesPage$ = componentWhileOpen(this.priceTablesModal.open$, this.overlayLoaders.priceTables);

    // US #62189 (decision 1): the drawer shows the Tabela de Preços leaf only after TABELA_PRECO answers true;
    // until then, on a refusal and on a failing probe it stays out (fail-closed). Restricted to that key: the other
    // leaves of the drawer keep their behaviour and query nothing.
    const priceTablesGranted$ = this.userAccess.check(PRICE_TABLES_PERMISSION).pipe(
      catchError(() => of(false)),
      startWith(false),
      // One repaint when the answer confirms the hidden start, two only when it shows the leaf.
      distinctUntilChanged(),
    );

    this.menuItems$ = combineLatest([this.state.localizedModules$, priceTablesGranted$]).pipe(
      map(([modules, priceTablesGranted]) =>
        this.withoutGatedLeaves(this.mapModulesToMenuItems(modules), priceTablesGranted)),
      catchError(() => of([] as MenuItem[])),
      shareReplay(1),
    );
  }

  toggleSidebar(): void {
    if (this.viewport.isMobile() || this.viewport.isTablet()) {
      this.menuDrawer.toggle();
      return;
    }
    this.sidebarOpen = !this.sidebarOpen;
  }

  openSidebar(): void {
    this.sidebarOpen = true;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }

  get currentUrl(): string {
    return this.router.url.split('?')[0];
  }

  // ── Backend menu → MenuItem mapping ───────────────────────────────────
  //
  // Mirror of the helpers in `SidebarComponent`. Kept duplicated here on
  // purpose: the sidebar is rendered only on desktop and the mobile drawer
  // gets its own input. Sharing the helpers across both would force a
  // refactor of the sidebar (out of scope for this branch).

  /** Drops the Tabela de Preços leaf, at any depth, while its permission is not granted. */
  private withoutGatedLeaves(items: MenuItem[], priceTablesGranted: boolean): MenuItem[] {
    if (priceTablesGranted) {
      return items;
    }
    return items
      .filter(item => item.key !== SUBMODULE_KEY_CADASTRO_TABELA_PRECO)
      .map(item => (item.children ? { ...item, children: this.withoutGatedLeaves(item.children, false) } : item));
  }

  private mapModulesToMenuItems(modules: LocalizedModuleDto[]): MenuItem[] {
    return modules.map(module => this.mapModuleToMenuItem(module));
  }

  private mapModuleToMenuItem(module: LocalizedModuleDto): MenuItem {
    const iconSvg = backendIconToLucide(module.icon ?? undefined);
    const submodules = module.submodules ?? [];
    const route = backendRouteToAngular(module.route ?? '') || getDefaultRouteForModuleName(module.name) || '';

    if (submodules.length === 0) {
      return {
        id: module.key,
        key: module.key,
        label: module.name,
        route: route || undefined,
        iconSvg,
      };
    }

    return {
      id: module.key,
      key: module.key,
      label: module.name,
      route: route || undefined,
      iconSvg,
      children: submodules.map(sub => this.mapSubmoduleToMenuItem(sub)),
    };
  }

  private mapSubmoduleToMenuItem(submodule: LocalizedSubmoduleDto): MenuItem {
    const iconSvg = backendIconToLucide(submodule.icon ?? undefined);
    const route = backendRouteToAngular(submodule.route ?? '') || '';
    const children = submodule.children ?? [];

    if (children.length === 0) {
      return {
        id: submodule.key,
        key: submodule.key,
        label: submodule.name,
        route: route || undefined,
        iconSvg,
      };
    }

    return {
      id: submodule.key,
      key: submodule.key,
      label: submodule.name,
      route: route || undefined,
      iconSvg,
      children: children.map(c => this.mapSubmoduleToMenuItem(c)),
    };
  }
}
